import { type FC, useRef, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { Box, Container, DialogHeader, Stack, Tab, Tabs } from '@packages/shared';

import { ResponsiveNavigationMainItem } from './ResponsiveNavigationMainItem';
import type { NavigationItem, RawNavigationItem } from './navigationTypes';
import { useCleanedNavData } from './useCleanedNavData';
import type { UseTreeNavigationPopupOptions } from './Flyout/useTreeNavigationPopup';
import { useTreeNavigationPopup } from './Flyout/useTreeNavigationPopup';
import { useNavigationTracking } from './useNavigationTracking';

const ResponsiveFlyout = dynamic(() => import('./Flyout/ResponsiveFlyout'), {
  ssr: false,
});

export type ResponsiveNavigationProps = {
  /** The nav tree, as a polyforest (in the graph theory sense) of navigation items */
  items: RawNavigationItem[];
} & Pick<UseTreeNavigationPopupOptions, 'mode' | 'onFirstInteraction'>;

const MobilePlaceholderStuff = () => (
  <Stack sx={{ display: { lg: 'none' } }}>
    <DialogHeader title="test dialog header" onBack={() => {}} />
    <Tabs value={0} variant="fullWidth">
      <Tab label="Menü" />
      <Tab label="Mein Konto" />
    </Tabs>
  </Stack>
);

/**
 * Navigation component, which displays a horizontal navigation bar on desktop. Shows {@link ResponsiveFlyout} on user interaction, to enable navigating the nav tree.
 *
 * If possible, mobile navigation will be implemented later as a responsive variant of this component, instead of as a different JS-switched component, to avoid layout shifts on page load and other issues with having distinct components for each breakpoint.
 */
export const ResponsiveNavigation: FC<ResponsiveNavigationProps> = ({
  items,
  mode = 'click',
  onFirstInteraction,
}) => {
  const popupAnchor = useRef<HTMLDivElement>(null);

  const { asPath } = useRouter();
  const firstRender = useRef(true);
  const {
    navigationDirection,
    showPopup,
    isSelected,
    isRoot,
    selectMainItem,
    selectPopupItem,
    backToParent,
    closePopup,
  } = useTreeNavigationPopup({ mode, onFirstInteraction });

  // close popup when route changes, just in case the user manages to navigate without closing the navigation
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    closePopup();
  }, [asPath, closePopup]); // `closePopup` is memoized specifically to work as a dependency here, the other functions are not

  const { onNavigate } = useNavigationTracking();

  const handleNavigate = (item: NavigationItem) => {
    onNavigate(item);
    closePopup();
  };

  const { cleanItems, flatItems } = useCleanedNavData(items);

  const selectedItem = flatItems.find(isSelected);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplate: '"everything" auto / 100%',
      }}
      ref={popupAnchor}
    >
      <Box
        sx={{
          borderTop: '1px solid',
          borderBottom: '1px solid',
          gridArea: 'everything',
          borderColor: (theme) => theme.palette.grey.main,
        }}
      />
      {/* padding is necessary to work around Container padding in MUI, which changes at different breakpoints (both default and theme-customized for some reason). media query (xs) is needed because the MUI padding has higher specificity otherwise, as it uses a media query. */}
      <Container sx={{ gridArea: 'everything', padding: { xs: 0 } }}>
        <Stack component="nav" id="main-navigation">
          <MobilePlaceholderStuff />
          <Stack
            component="ul"
            direction="row"
            sx={{
              margin: 0,
              padding: 0,
              flexWrap: 'wrap',
              minHeight: (theme) => `calc(${theme.spacing(5.5)} + 2px)`,
              maxHeight: (theme) => `calc(${theme.spacing(5.5)} + 2px)`,
              overflow: 'hidden',
              listStyle: 'none',
            }}
            onMouseLeave={(event) => {
              if (mode !== 'hover') return;

              const mouseIsEnteringPopup =
                event.relatedTarget instanceof Node &&
                document.querySelector('#main-navigation-flyout')?.contains(event.relatedTarget);

              if (!mouseIsEnteringPopup) {
                closePopup();
              }
            }}
          >
            {cleanItems.map((item) => (
              <ResponsiveNavigationMainItem
                key={item.uri}
                href={item.uri}
                allowNavigation={mode === 'hover' || item.children.length === 0}
                isOpen={showPopup && isRoot(item)}
                // trailing slash is stripped for the comparison, because during SSR `asPath` has no trailing slash, but the `uri` prop does, as does `asPath` on the client
                isActive={item.uri !== undefined && asPath.startsWith(item.uri.replace(/\/$/, ''))}
                onHover={() => {
                  if (mode === 'hover') {
                    selectMainItem(item, true);
                  }
                }}
                onClick={() => selectMainItem(item)}
                onNavigate={() => handleNavigate(item)}
              >
                {item.displayName}
              </ResponsiveNavigationMainItem>
            ))}
          </Stack>
          <ResponsiveFlyout
            open={showPopup}
            anchorEl={popupAnchor.current}
            item={selectedItem}
            direction={navigationDirection}
            onBack={selectedItem && isRoot(selectedItem) ? undefined : backToParent}
            onClose={closePopup}
            onItemClick={selectPopupItem}
            onNavigate={handleNavigate}
            onMouseLeave={(event) => {
              if (mode !== 'hover') return;

              const mouseIsEnteringNavbar =
                event.relatedTarget instanceof Node &&
                document.querySelector('nav#main-navigation')?.contains(event.relatedTarget);

              if (!mouseIsEnteringNavbar) {
                closePopup();
              }
            }}
          />
        </Stack>
      </Container>
    </Box>
  );
};
